import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { Phone, Calendar, MessageSquare, Globe } from "lucide-react";
import HospitalityUseCase from "../use-cases/HospitalityUseCase";
import { useTranslation } from "@/hooks/useTranslation";

const VacationRentalUseCase = () => {
  const { t } = useTranslation();

  const benefits = [
    {
      icon: Phone,
      title: t("vacationRental.benefits.checkin.title"),
      description: t("vacationRental.benefits.checkin.description")
    },
    {
      icon: MessageSquare,
      title: t("vacationRental.benefits.support.title"),
      description: t("vacationRental.benefits.support.description")
    },
    {
      icon: Calendar,
      title: t("vacationRental.benefits.booking.title"),
      description: t("vacationRental.benefits.booking.description")
    },
    {
      icon: Globe,
      title: t("vacationRental.benefits.local.title"),
      description: t("vacationRental.benefits.local.description")
    }
  ];

  const useCaseData = {
    title: t("vacationRental.title"),
    description: t("vacationRental.subtitle"),
    details: [
      { label: "Industry", value: "Hospitality" },
      { label: "Language", value: "English (USA)" },
      { label: "Function", value: "Guest Services" }
    ],
    audioSrc: "/HostCall-ai-welcome-2025-05.mp3"
  };

  return (
    <section className="section-padding bg-muted/30">
      <div className="container mx-auto px-4">
        <ScrollReveal direction="up" duration={700}>
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
            <span className="inline-block bg-primary/10 text-primary px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-3 md:mb-4">
              {t("vacationRental.badge")}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
              {t("vacationRental.title")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground px-2">
              {t("vacationRental.subtitle")}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left side - Audio Player Demo */}
          <ScrollReveal direction="left" delay={200}>
            <div className="max-w-md mx-auto lg:mx-0">
              <HospitalityUseCase
                title={useCaseData.title}
                description={useCaseData.description}
                details={useCaseData.details}
                audioSrc={useCaseData.audioSrc}
              />
            </div>
          </ScrollReveal>

          {/* Right side - Benefits */}
          <ScrollReveal direction="right" delay={300}>
            <div className="space-y-4 md:space-y-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-background rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                    <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground mb-1 text-sm sm:text-base">{benefit.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              ))}

              <div className="pt-2 sm:pt-4">
                <Link to="/demo">
                  <Button size="lg" className="gradient-bg w-full sm:w-auto">
                    {t("vacationRental.cta")}
                  </Button>
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default VacationRentalUseCase;
